import {getLogger} from '@/shared/utils'
import {setLocalStorageItemWithQuotaRetry} from '@/shared/utils/localStorageQuota'
import {Auth} from 'aws-amplify'
import {CognitoUserSession} from 'amazon-cognito-identity-js'

const getUserIdentityId = (): string | undefined => {
  const user = localStorage.getItem('cognito-user')
  if (user) {
    const userObj = JSON.parse(user)
    return userObj.attributes['custom:identityId']
  }
}

/**
 * Uses Cognito refresh token to obtain new access/id tokens (no SMS step).
 * @returns whether refresh succeeded and localStorage session was updated
 */
export async function tryRefreshCognitoSession(): Promise<boolean> {
  getLogger().info('Try refresh Cognito session')
  try {
    const cognitoUser = await Auth.currentAuthenticatedUser()
    const currentSession = await Auth.currentSession()
    await new Promise<void>((resolve, reject) => {
      cognitoUser.refreshSession(
        currentSession.getRefreshToken(),
        (err: unknown, session: CognitoUserSession) => {
          if (err) {
            reject(err instanceof Error ? err : new Error(String(err)))
            return
          }
          const stored = localStorage.getItem('cognito-user')
          if (stored) {
            const next = JSON.stringify({
              ...JSON.parse(stored),
              signInUserSession: {
                clockDrift: 1,
                idToken: session.getIdToken(),
                refreshToken: session.getRefreshToken(),
                accessToken: session.getAccessToken(),
              },
            })
            if (!setLocalStorageItemWithQuotaRetry('cognito-user', next)) {
              getLogger().error(
                'Could not persist refreshed Cognito session (localStorage quota).'
              )
              reject(new Error('Could not persist refreshed session'))
              return
            }
          }
          resolve()
        }
      )
    })
    return true
  } catch (error: unknown) {
    getLogger().error(
      `tryRefreshCognitoSession failed: ${error instanceof Error ? error.message : String(error)}`
    )
    return false
  }
}

const refreshToken = async () => {
  await tryRefreshCognitoSession()
}

export {getUserIdentityId, refreshToken}
